import { startOfDay } from 'date-fns';
import { DatePicker, GridAlpha, NumberInput, Switch, useToasts } from '@applyboard/crystal-ui';
import { useTranslation } from '@applyboard/ui-utils';
import { zodValidateField } from '../../../../common/schema/forms';
import {
  GmatAnalyticalWritingSchema,
  GmatDataInsightsSchema,
  GmatIntegratedReasoningSchema,
  GmatQuantitativeReasoningSchema,
  GmatRankSchema,
  GmatTotalScoreSchema,
  GmatVerbalReasoningSchema,
  dateOfExamSchema,
} from '../../formSchemas';
import { Field, useFieldValues } from '.';

interface GMATScoreFieldsProps {
  hasGmat: boolean;
}

const GMATScoreFields = ({ hasGmat }: GMATScoreFieldsProps) => {
  const { t } = useTranslation();
  const toasts = useToasts();

  if (!hasGmat) {
    return null;
  }

  return (
    <>
      <GridAlpha columns="repeat(27, 1fr)" rowGap={6} pb={6}>
        <GridAlpha.Item columnStart={1} columnEnd={{ xs: 28, md: 14 }}>
          <Field
            as={NumberInput}
            name="gmatTotalScore"
            label={t('loans.educationSummary.totalScore')}
            required={t('loans.educationSummary.errors.totalScoreFieldRequired')}
            validate={value => {
              return zodValidateField(
                GmatTotalScoreSchema(t),
                t('loans.educationSummary.totalScore'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 1, md: 15 }} columnEnd={28}>
          <Field
            as={NumberInput}
            name="gmatTotalRank"
            label={t('loans.educationSummary.totalRank')}
            required={t('loans.educationSummary.errors.totalRankFieldRequired')}
            validate={value => {
              return zodValidateField(
                GmatRankSchema(t),
                t('loans.educationSummary.totalRank'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={1} columnEnd={{ xs: 28, md: 14 }}>
          <Field
            as={NumberInput}
            name="gmatVerbalScore"
            label={t('loans.educationSummary.verbalScore')}
            required={t('loans.educationSummary.errors.verbalScoreFieldRequired')}
            validate={value => {
              return zodValidateField(
                GmatVerbalReasoningSchema(t),
                t('loans.educationSummary.verbalScore'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 1, md: 15 }} columnEnd={28}>
          <Field
            as={NumberInput}
            name="gmatVerbalRank"
            label={t('loans.educationSummary.verbalRank')}
            required={t('loans.educationSummary.errors.verbalRankFieldRequired')}
            validate={value => {
              return zodValidateField(
                GmatRankSchema(t),
                t('loans.educationSummary.verbalRank'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={1} columnEnd={{ xs: 28, md: 14 }}>
          <Field
            as={NumberInput}
            name="gmatQuantitativeScore"
            label={t('loans.educationSummary.quantitativeScore')}
            required={t('loans.educationSummary.errors.quantitativeScoreFieldRequired')}
            validate={value => {
              return zodValidateField(
                GmatQuantitativeReasoningSchema(t),
                t('loans.educationSummary.quantitativeScore'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 1, md: 15 }} columnEnd={28}>
          <Field
            as={NumberInput}
            name="gmatQuantitativeRank"
            label={t('loans.educationSummary.quantitativeRank')}
            required={t('loans.educationSummary.errors.quantitativeRankFieldRequired')}
            validate={value => {
              return zodValidateField(
                GmatRankSchema(t),
                t('loans.educationSummary.quantitativeRank'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>

        <GridAlpha.Item columnStart={1} columnEnd={{ xs: 28, md: 14 }}>
          <Field
            as={NumberInput}
            name="gmatDataInsightsScore"
            label={t('loans.educationSummary.dataInsightsScore')}
            validate={value => {
              return zodValidateField(
                GmatDataInsightsSchema(t),
                t('loans.educationSummary.dataInsightsScore'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 1, md: 15 }} columnEnd={28}>
          <Field
            as={NumberInput}
            name="gmatDataInsightsRank"
            label={t('loans.educationSummary.dataInsightsRank')}
            validate={value => {
              return zodValidateField(
                GmatRankSchema(t),
                t('loans.educationSummary.dataInsightsRank'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>

        <GridAlpha.Item columnStart={1} columnEnd={{ xs: 28, md: 14 }}>
          <Field
            as={NumberInput}
            name="gmatAwaScore"
            label={t('loans.educationSummary.awaScore')}
            validate={value => {
              return zodValidateField(
                GmatAnalyticalWritingSchema(t),
                t('loans.educationSummary.awaScore'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 1, md: 15 }} columnEnd={28}>
          <Field
            as={NumberInput}
            name="gmatAwaRank"
            label={t('loans.educationSummary.awaRank')}
            validate={value => {
              return zodValidateField(
                GmatRankSchema(t),
                t('loans.educationSummary.awaRank'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={1} columnEnd={{ xs: 28, md: 14 }}>
          <Field
            as={NumberInput}
            name="gmatIntegratedReasoningScore"
            label={t('loans.educationSummary.integratedReasoningScore')}
            validate={value => {
              return zodValidateField(
                GmatIntegratedReasoningSchema(t),
                t('loans.educationSummary.integratedReasoningScore'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={{ xs: 1, md: 15 }} columnEnd={28}>
          <Field
            as={NumberInput}
            name="gmatIntegratedReasoningRank"
            label={t('loans.educationSummary.integratedReasoningRank')}
            validate={value => {
              return zodValidateField(
                GmatRankSchema(t),
                t('loans.educationSummary.integratedReasoningRank'),
                value,
                toasts,
              );
            }}
          />
        </GridAlpha.Item>
        <GridAlpha.Item columnStart={1} columnEnd={{ xs: 28, md: 14 }}>
          <Field
            as={DatePicker}
            name="gmatDateOfExam"
            label={t('loans.educationSummary.dateOfExam')}
            required={t('loans.educationSummary.errors.dateOfExamRequired')}
            validate={(value: string) => {
              return zodValidateField(
                dateOfExamSchema(t),
                t('loans.educationSummary.dateOfExam'),
                value,
                toasts,
              );
            }}
            granularity="day"
            minDate="1900-01-01T00:00:00.000Z"
            maxDate={startOfDay(new Date()).toISOString()}
            size="md"
          />
        </GridAlpha.Item>
      </GridAlpha>
    </>
  );
};

const GMATScores = () => {
  const { hasGmat } = useFieldValues(['hasGmat']);
  const { t } = useTranslation();

  return (
    <GridAlpha columns="repeat(27, 1fr)" rowGap={2} pb={3}>
      <GridAlpha.Item columnStart={{ xs: 1, md: 2 }} columnEnd={{ xs: 28, md: 27 }}>
        <Field
          as={Switch}
          name="hasGmat"
          label={t('loans.educationSummary.gmatSwitchLabel')}
          labelPlacement="left"
        />
      </GridAlpha.Item>
      <GridAlpha.Item columnStart={{ xs: 1, md: 2 }} columnEnd={{ xs: 28, md: 27 }}>
        <GMATScoreFields hasGmat={hasGmat} />
      </GridAlpha.Item>
    </GridAlpha>
  );
};

export default GMATScores;
